import { ChevronDown } from "lucide-react";
import { type JSX } from "react";
import { cn } from "./lib/utils";
import { NavItem } from "./NavItem";
import { NavIcon } from "./NavIcon";
import type { NavGroup as NavGroupModel } from "./nav-model";

export interface NavGroupProps {
  group: NavGroupModel;
  activePathname: string;
  /** Sidebar rail state, not the group's own open/closed state. */
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (route: string) => void;
}

function NavGroupItems({
  group,
  activePathname,
  collapsed,
  onNavigate
}: Pick<NavGroupProps, "group" | "activePathname" | "collapsed" | "onNavigate">): JSX.Element {
  return (
    <ul
      className={cn(
        // px-0/pb-0 rather than p-0 so pt-1 can't be beaten by shorthand ordering;
        // the horizontal reset is still needed to kill the UA list indent.
        "m-0 flex list-none flex-col gap-0.5 px-0 pt-1 pb-0",
        collapsed && "items-center"
      )}
    >
      {group.items.map((item) => (
        <li key={item.id}>
          <NavItem
            item={item}
            active={item.route === activePathname}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * A nav section within an app. Groups that carry a label render a collapsible
 * header (icon + label + chevron) that centres to an icon-only control in the
 * rail, matching the nav items. Ungrouped items render as a plain list.
 */
export function NavGroup({
  group,
  activePathname,
  collapsed,
  expanded,
  onToggle,
  onNavigate
}: NavGroupProps): JSX.Element {
  const items = (
    <NavGroupItems
      group={group}
      activePathname={activePathname}
      collapsed={collapsed}
      onNavigate={onNavigate}
    />
  );

  if (!group.label || collapsed) {
    return items;
  }

  const header = (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      data-platform-nav-group-toggle=""
      data-nav-group-id={group.id}
      className={cn(
        // font-body is explicit: <button> does not inherit the UA font, and the
        // chrome reset no longer forces `font: inherit`.
        "group/nav-group flex h-8 items-center gap-2 rounded-pill font-body text-xs uppercase",
        "transition-colors duration-150 ease-out",
        "text-sidebar-text-muted hover:bg-surface-muted hover:text-sidebar-text",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
        "w-full px-3"
      )}
    >
      {group.icon ? <NavIcon icon={group.icon} className="size-4 shrink-0 object-contain" /> : null}
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-start">{group.label}</span>
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 opacity-0 transition-[opacity,transform] group-hover/nav-group:opacity-100 group-focus-visible/nav-group:opacity-100",
              !expanded && "-rotate-90"
            )}
            aria-hidden="true"
          />
        </>
      )}
    </button>
  );

  return (
    <div className="flex flex-col">
      {header}

      {/* 0fr -> 1fr keeps the open/close transition smooth, as elsewhere in the chrome. */}
      <div
        aria-hidden={!expanded}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">{items}</div>
      </div>
    </div>
  );
}
